import {useCan, useCanAll} from 'src/permissions/hooks';
import {FC, useState} from 'react';
import {Edit, PhotoCameraOutlined} from '@mui/icons-material';
import {Button} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import {usersApi} from 'src/api';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {AppDispatch} from 'src/app/store';
import {ColorField} from 'src/components/common/ColorField';
import {DropZoneModal} from 'src/components/common/DropZoneModal';
import AMPModal from 'src/components/common/AMPModal';
import {ManagersForm} from 'src/components/forms/ManagersForm';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {isErrorObject} from 'src/utils/helper';
import {blankPhoto} from 'src/utils/consts';
import {NotifyError} from 'src/utils/notification';
import {TechnicalTeamDetailsProps} from './TechnicalTeamDetails.types';
import {UserResetWrapper} from '../../users/UserResetWrapper';
import {UserIdentityCard} from '../../users/UserIdentityCard';
import {buildUserFacts} from '../../users/userProfile';
import {actionButtonSx} from 'src/components/common/actionButton.sx';

type AddUserAvatarMutationTrigger = ReturnType<typeof usersApi.useSetUserAvatarMutation>[0];

const handleTrigger = async (
    file: File[],
    userId: number,
    trigger: AddUserAvatarMutationTrigger,
    dispatch: AppDispatch,
    handleCloseImageModal: () => void,
    handleBlockSubmit: (status: boolean) => void,
    _businessId?: number
) => {
    try {
        handleBlockSubmit(true);
        dispatch(preloaderChangeStatus(true));
        const formData = new FormData();
        formData.append('file', file[0]);
        await trigger({data: formData, userId}).unwrap();
        handleCloseImageModal();
    } catch (error) {
        if (isErrorObject(error)) {
            NotifyError(error.data.message);
        }
    } finally {
        dispatch(preloaderChangeStatus(false));
        handleBlockSubmit(false);
    }
};

export const TechnicalTeamDetails: FC<TechnicalTeamDetailsProps> = ({data}) => {
    const intl = useIntl();
    const {authInfo} = useAppSelector((state) => state.common);
    const canUpdateAvatar = useCan('profile.updateAvatar');
    // Back-office staff had no editor at all: they were created with a name, a kind and a language and
    // could change none of them. The pair is what the endpoint asks for, from the days when editing
    // somebody rebound the role their kind started them with; it no longer does, and narrowing it to
    // users.update alone is a decision about who may edit whom rather than a tidy-up.
    const canEditManager = useCanAll('users.update', 'accessControl.manageBindings');
    const [showEditModal, setShowEditModal] = useState(false);
    const [openDropZone, setOpenDropZone] = useState<boolean>(false);
    const [userImage, setUserImage] = useState<File[]>();
    const canReadResetPassword = useCan('users.update');

    const uploadDialogTitle = intl.formatMessage({id: 'addNewImage'});
    const editDialogTitle = intl.formatMessage({id: 'editManager'});

    const dispatch = useAppDispatch();
    const [trigger] = usersApi.useSetUserAvatarMutation();

    const [blockSubmit, setBlockSubmit] = useState(false);

    const handleBlockSubmit = (status: boolean) => {
        setBlockSubmit(status);
    };

    const handleCloseImageModal = () => {
        setOpenDropZone(false);
    };

    const handleSetImage = (file: File[] | null) => {
        file && setUserImage(file);
    };

    const handleSubmit = () => {
        userImage &&
        handleTrigger(
            userImage,
            data.id,
            trigger,
            dispatch,
            handleCloseImageModal,
            handleBlockSubmit,
            data.business.id
        );
    };

    return (
        <>
            <UserIdentityCard
                data={data}
                title={`${data.firstName || ''} ${data.lastName || ''}`.trim() || data.emailAddress || ''}
                typeLabel={String(data.userType)}
                facts={buildUserFacts(data)}
                actions={(
                    <>
                        {canUpdateAvatar && (
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<PhotoCameraOutlined/>}
                                onClick={() => setOpenDropZone(true)}
                            >
                                <FormattedMessage id="photo"/>
                            </Button>
                        )}
                        {canEditManager && (
                            <Button
                                variant="contained"
                                sx={actionButtonSx('32px', 'secondary')}
                                startIcon={<Edit/>}
                                onClick={() => setShowEditModal(true)}
                            >
                                <FormattedMessage id="edit"/>
                            </Button>
                        )}
                        {(canReadResetPassword || data.id === authInfo.id) && (
                            <UserResetWrapper
                                data={data}
                                toolbar
                            />
                        )}
                    </>
                )}
            />

            <DropZoneModal
                submitIsBlocked={blockSubmit}
                handleClose={handleCloseImageModal}
                open={openDropZone}
                title={uploadDialogTitle}
                handleFile={handleSetImage}
                handleSubmit={handleSubmit}
                isImage
            />
            <AMPModal
                handleClose={() => setShowEditModal(false)}
                open={showEditModal}
                title={editDialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <ManagersForm
                    handleClose={() => setShowEditModal(false)}
                    businessId={data.business.id}
                    isEditMode
                    userId={data.id}
                    initialValues={{
                        firstName: data.firstName ?? '',
                        lastName: data.lastName ?? '',
                        email: data.emailAddress ?? '',
                        userType: data.userType ?? '',
                        language: data.language ?? '',
                        title: data.title ?? '',
                        phoneNumber: data.phoneNumber ?? '',
                    }}
                />
            </AMPModal>
        </>
    );
};
